/**
 * Lens cart guard
 *
 * Companion script of the lens color picker (see assets/lens-color-picker.js
 * and docs/CUSTOM-LENS-OPTIONS.md). The picker adds lens supplements (color
 * and/or lens type) to the cart as separate line items, tied to their frame
 * through '_lcp_group' (one group per add), with '_monture' retained on
 * supplements. The frame records its expected variant ids in '_lcp_components'.
 *
 * This guard keeps those supplement lines consistent with their frame:
 *   1. Quantity sync - if the customer changes the frame quantity in the
 *      cart, the matching supplement lines follow automatically.
 *   2. Orphan removal - if the frame is removed from the cart, its
 *      supplement lines are removed too in the supported JavaScript flow.
 *   3. Locked controls - the quantity selector and "Remove" link are hidden
 *      on supplement lines, so they can only be managed through their frame.
 *
 * New groups never match ungrouped frames or another add of the same frame.
 * Legacy carts cannot be upgraded reliably from their partial properties:
 * keep their quantities and controls, and ask for a new selection at checkout.
 * This is a browser guard, not server-side checkout validation.
 *
 * Loaded globally (see layout/theme.liquid) since the cart drawer can appear
 * on any page. Reuses the theme's globals: theme.routes.cart /
 * theme.routes.cartUpdate and the 'cart-form' events ('on:cart:change',
 * 'on:cart:after-merge').
 */
/* global theme */

(() => {
  if (window.lensCartGuard) return;

  const guard = {
    enforcing: false,
    pending: false,
    enforcementPromise: null,
    checkingCheckout: false,

    getErrorMessage(code) {
      const messages = {
        fr: {
          add: 'La personnalisation n’a pas pu être confirmée. Vérifiez votre panier avant de réessayer.',
          incomplete: 'Les options de verres de ce panier doivent être resélectionnées. Retirez la monture concernée et ses suppléments, puis ajoutez-les à nouveau depuis la fiche produit.',
          network: 'Impossible de vérifier votre panier. Réessayez dans un instant.',
          updating: 'Le panier se met à jour. Réessayez dans un instant.',
          unavailable: 'Indisponible'
        },
        en: {
          add: 'Your lens selection could not be confirmed. Check your cart before trying again.',
          incomplete: 'Please select the lenses again. Remove the affected frame and its supplements, then add them again from the product page.',
          network: 'Your cart could not be checked. Please try again shortly.',
          updating: 'Your cart is updating. Please try again shortly.',
          unavailable: 'Unavailable'
        },
        es: {
          add: 'No se ha podido confirmar la selección de lentes. Revisa tu carrito antes de volver a intentarlo.',
          incomplete: 'Selecciona de nuevo las lentes. Elimina la montura afectada y sus suplementos y vuelve a añadirlos desde la página del producto.',
          network: 'No se ha podido comprobar tu carrito. Vuelve a intentarlo en unos instantes.',
          updating: 'Tu carrito se está actualizando. Vuelve a intentarlo en unos instantes.',
          unavailable: 'No disponible'
        }
      };
      const language = document.documentElement.lang.toLowerCase().split('-')[0];
      return (messages[language] || messages.en)[code];
    },

    /**
     * @returns {Promise<Object>} Current cart (/cart.js).
     */
    async getCart() {
      const response = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Lens cart guard: cart fetch failed (${response.status}).`);
      return response.json();
    },

    /**
     * @param {Object} item - Cart line item (/cart.js format).
     * @returns {boolean} Whether the line is a lens supplement.
     */
    isSupplement(item) {
      return Boolean(item.properties && item.properties._monture);
    },

    /**
     * @param {Object} item - Cart line item.
     * @returns {Array} Non-technical [name, value] property pairs.
     */
    visibleProperties(item) {
      return Object.entries(item.properties || {})
        .filter(([name, value]) => value && !name.startsWith('_'));
    },

    /**
     * @param {Object} supplement - Supplement line item.
     * @param {Object} item - Candidate frame line item.
     * @returns {boolean} Whether the item is the supplement's frame.
     */
    isFrameOf(supplement, item) {
      if (this.isSupplement(item)) return false;
      if (String(item.variant_id) !== String(supplement.properties._monture)) return false;
      const itemProperties = item.properties || {};
      if (supplement.properties._lcp_group || itemProperties._lcp_group) {
        return Boolean(supplement.properties._lcp_group)
          && supplement.properties._lcp_group === itemProperties._lcp_group;
      }
      return this.visibleProperties(supplement)
        .every(([name, value]) => itemProperties[name] === value);
    },

    /**
     * Checks both presence and quantities. A missing supplement must never
     * make a customized frame eligible for the supported checkout flow.
     */
    isGroupComplete(cart, groupId) {
      const lines = cart.items.filter((item) => item.properties && item.properties._lcp_group === groupId);
      const frames = lines.filter((item) => !this.isSupplement(item));
      const supplements = lines.filter((item) => this.isSupplement(item));
      if (!frames.length) return false;

      let expected;
      try {
        expected = JSON.parse(frames[0].properties._lcp_components);
      } catch (error) {
        return false;
      }
      if (!Array.isArray(expected) || expected.length < 1 || expected.length > 2
        || expected.some((id) => typeof id !== 'string' || !/^[1-9]\d*$/.test(id))
        || new Set(expected).size !== expected.length) return false;

      if (frames.some((item) => String(item.variant_id) !== String(frames[0].variant_id)
        || item.properties._lcp_components !== frames[0].properties._lcp_components)) return false;
      if (lines.some((item) => !Number.isSafeInteger(item.quantity) || item.quantity < 1)) return false;
      if (supplements.some((item) => !expected.includes(String(item.variant_id)) || !this.isFrameOf(item, frames[0]))) return false;

      const quantity = frames.reduce((sum, item) => sum + item.quantity, 0);
      return expected.every((id) => supplements
        .filter((item) => String(item.variant_id) === id)
        .reduce((sum, item) => sum + item.quantity, 0) === quantity);
    },

    isCartComplete(cart) {
      if (cart.items.some((item) => this.isSupplement(item) && !item.properties._lcp_group)) return false;
      const groups = new Set(cart.items.map((item) => item.properties && item.properties._lcp_group).filter(Boolean));
      return Array.from(groups).every((groupId) => this.isGroupComplete(cart, groupId));
    },

    /**
     * Aligns every supplement line's quantity on its frame's quantity
     * (0 when the frame is gone), then refreshes the cart UI if needed.
     */
    async enforce() {
      if (this.enforcementPromise) {
        this.pending = true;
        return this.enforcementPromise;
      }
      this.enforcing = true;

      this.enforcementPromise = (async () => {
        let complete = false;
        do {
          this.pending = false;
          complete = await this.reconcileCart();
        } while (this.pending);
        return complete;
      })().catch(() => {
        this.showCartError(this.getErrorMessage('network'));
        return false;
      }).finally(() => {
        this.enforcing = false;
        this.enforcementPromise = null;
      });
      return this.enforcementPromise;
    },

    async reconcileCart() {
      let cart = await this.getCart();
      const updates = {};
      const visited = new Set();

      cart.items
        .filter((item) => this.isSupplement(item))
        .forEach((supplement) => {
          // Historical lines do not contain enough information to safely
          // recalculate a price-bearing quantity. Leave them user-editable.
          if (!supplement.properties._lcp_group || visited.has(supplement.key)) return;
          const frames = cart.items.filter((item) => this.isFrameOf(supplement, item));
          if (frames.length) {
            try {
              const expected = JSON.parse(frames[0].properties._lcp_components);
              if (!Array.isArray(expected) || !expected.includes(String(supplement.variant_id))
                || frames.some((item) => item.properties._lcp_components !== frames[0].properties._lcp_components
                  || !Number.isSafeInteger(item.quantity) || item.quantity < 1)) return;
            } catch (error) {
              return;
            }
          }
          const frameQuantity = frames.reduce((sum, item) => sum + item.quantity, 0);
          const siblings = cart.items.filter((item) => this.isSupplement(item)
            && item.properties._lcp_group === supplement.properties._lcp_group
            && String(item.variant_id) === String(supplement.variant_id));
          siblings.forEach((item) => visited.add(item.key));
          // Discounts can split one variant into multiple cart lines.
          let difference = frameQuantity - siblings.reduce((sum, item) => sum + item.quantity, 0);
          siblings.forEach((item) => {
            const adjustment = difference > 0 ? difference : Math.max(difference, -item.quantity);
            if (adjustment) updates[item.key] = item.quantity + adjustment;
            difference -= adjustment;
          });
        });

      if (Object.keys(updates).length > 0) {
        for (const [id, quantity] of Object.entries(updates)) {
          const response = await fetch(`${theme.routes.cart}/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id, quantity })
          });
          if (!response.ok) throw new Error(`Lens cart guard: cart update failed (${response.status}).`);
        }

        cart = await this.getCart();
        this.refreshCartUI();
      }

      const complete = this.isCartComplete(cart);
      this.syncCheckoutButtons(cart);
      this.showCartError(complete ? '' : this.getErrorMessage('incomplete'));
      return complete;
    },

    /**
     * Refreshes the cart display after a quantity fix: through the theme's
     * AJAX cart when available, with a page reload otherwise (cart page
     * without AJAX updates). The reload cannot loop: once quantities are
     * consistent, enforce() makes no further update.
     */
    refreshCartUI() {
      if (document.querySelector('cart-form[data-ajax-update]')) {
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      } else if (document.querySelector('.cart-item')) {
        window.location.reload();
      }
    },

    /**
     * Hides the quantity selector and "Remove" link on supplement lines
     * (cart page and cart drawer) - their quantity is guard-managed.
     */
    async lockSupplementLines() {
      this.syncCheckoutButtons(null);
      const inputs = document.querySelectorAll('.cart-item__quantity-input[data-key]');

      try {
        const cart = await this.getCart();
        this.syncCheckoutButtons(cart);
        const supplementKeys = new Set(
          cart.items.filter((item) => this.isSupplement(item) && item.properties._lcp_group).map((item) => item.key)
        );
        inputs.forEach((input) => {
          const column = input.closest('.cart-item__quantity');
          if (column) column.style.visibility = supplementKeys.has(input.dataset.key) ? 'hidden' : '';
        });
      } catch (error) {
        console.log(error); // eslint-disable-line
      }
    },

    /**
     * Wallet buttons bypass the cart form's submit event. Keep the theme's
     * normal checkout for lens carts; restore wallets for ordinary carts.
     */
    syncCheckoutButtons(cart) {
      const hide = !cart || cart.items.some((item) => this.isSupplement(item) || (item.properties && item.properties._lcp_group));
      document.querySelectorAll('.additional-checkout-buttons').forEach((container) => {
        if (hide) {
          if (!container.hasAttribute('data-lens-checkout-hidden')) container.setAttribute('data-lens-checkout-hidden', String(container.hidden));
          container.hidden = true;
        } else if (container.hasAttribute('data-lens-checkout-hidden')) {
          container.hidden = container.getAttribute('data-lens-checkout-hidden') === 'true';
          container.removeAttribute('data-lens-checkout-hidden');
        }
      });
    },

    showCartError(message) {
      document.querySelectorAll('cart-form').forEach((container) => {
        let error = container.querySelector('[data-lens-cart-error]');
        if (!error && message) {
          error = document.createElement('p');
          error.setAttribute('data-lens-cart-error', '');
          error.setAttribute('role', 'alert');
          container.prepend(error);
        }
        if (error) {
          error.textContent = message;
          error.hidden = !message;
        }
      });
    },

    /**
     * Gate the native cart form and drawer checkout link without bypassing
     * their existing handlers (including any terms agreement). Direct API
     * clients still require separate server-side protection.
     */
    async checkCheckout(evt) {
      if (evt.defaultPrevented || this.allowCheckout) return;
      let proceed;
      let checkoutForm;
      if (evt.type === 'submit' && evt.target instanceof HTMLFormElement
        && evt.target.closest('cart-form') && evt.submitter && evt.submitter.name === 'checkout') {
        const form = evt.target;
        checkoutForm = form;
        proceed = () => {
          const current = document.getElementById(form.id) || form;
          const button = current.querySelector('[name="checkout"]');
          if (current.isConnected && button) current.requestSubmit(button);
        };
      } else if (evt.type === 'click' && evt.target instanceof Element) {
        const link = evt.target.closest('cart-form a[href]');
        if (!link) return;
        const url = new URL(link.href, window.location.href);
        if (url.origin !== window.location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
        proceed = () => { if (link.isConnected) link.click(); };
      }
      if (!proceed) return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      if (this.checkingCheckout) return;
      this.checkingCheckout = true;
      try {
        if (await this.enforce()) {
          if (checkoutForm) {
            const current = document.getElementById(checkoutForm.id) || checkoutForm;
            const cart = await this.getCart();
            const inputs = Array.from(current.querySelectorAll('.cart-item__quantity-input[data-key]:not([disabled])'));
            // Native quantity changes are debounced. Never submit stale
            // updates[] values that could undo the group's reconciliation.
            if (inputs.some((input) => Number(input.value) !== cart.items.find((item) => item.key === input.dataset.key)?.quantity)) {
              this.showCartError(this.getErrorMessage('updating'));
              if (!inputs.some((input) => input.value !== input.dataset.initialValue)) this.refreshCartUI();
              return;
            }
          }
          this.allowCheckout = true;
          proceed();
        }
      } catch (error) {
        this.showCartError(this.getErrorMessage('network'));
      } finally {
        this.allowCheckout = false;
        this.checkingCheckout = false;
      }
    },

    init() {
      this.syncCheckoutButtons(null);
      // 'on:cart:change' fires after any theme cart mutation (quantity
      // change, removal, addition); 'on:cart:after-merge' fires after the
      // cart DOM has been re-rendered (controls need re-locking).
      document.addEventListener('on:cart:change', () => this.enforce());
      document.addEventListener('on:cart:after-merge', () => this.lockSupplementLines());
      document.addEventListener('submit', (evt) => this.checkCheckout(evt), true);
      document.addEventListener('click', (evt) => this.checkCheckout(evt), true);

      this.enforce().then(() => this.lockSupplementLines());
    }
  };

  window.lensCartGuard = guard;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => guard.init());
  } else {
    guard.init();
  }
})();
